import { Injectable, computed, inject, signal } from '@angular/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import type { BundleInfo } from '@capgo/capacitor-updater';
import { CapacitorHttp } from '@capacitor/core';
import { WEB_BUNDLE_VERSION } from '@ld.consumer-portal/constants';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { PlatformService } from './platform.service';
import { LoggingService } from './logging.service';
import { RemoteConfigService } from './remote-config.service';

/**
 * Manifest format served from Azure Blob Storage (latest.json).
 * The pipeline uploads this file alongside the bundle zip.
 */
export interface OtaManifest {
  /** Semantic version of the bundle (e.g. "1.2.1") */
  version: string;
  /** Full URL to the bundle zip file in Azure Blob Storage */
  url: string;
  /** SHA-256 checksum of the bundle zip for integrity verification */
  checksum: string;
  /**
   * Optional minimum native app version (NATIVE_APP_VERSION scheme, e.g. "2026.2.0")
   * required to run this bundle. Devices below it keep their current bundle and
   * must update through the app store.
   */
  minAppVersion?: string;
}

export type OtaUpdateStatus = 'idle' | 'checking' | 'downloading' | 'error' | 'disabled';

/**
 * OTA (Over-The-Air) Update Service
 *
 * Uses @capgo/capacitor-updater in **manual mode** (autoUpdate: false).
 * Instead of the plugin's built-in POST-based auto-update, this service:
 * 1. Fetches a static `latest.json` manifest via HTTP GET from Azure Blob Storage
 * 2. Compares versions to determine if an update is available
 * 3. Uses the plugin's download/set APIs to apply the update
 *
 * This approach avoids needing an Azure Function — just a static JSON file
 * in the same Blob Storage accounts already used for web deploys.
 *
 * The update is downloaded and applied immediately via CapacitorUpdater.set(),
 * which reloads the WebView in the current session.
 */
@Injectable({ providedIn: 'root' })
export class OtaUpdateService {
  private readonly platformService = inject(PlatformService);
  private readonly loggingService = inject(LoggingService);
  private readonly remoteConfigService = inject(RemoteConfigService);
  private readonly env = inject(ENVIRONMENT);

  /** Whether event listeners have been registered (prevents duplicate registration) */
  private listenersRegistered = false;

  /** Current status of the OTA update process */
  readonly status = signal<OtaUpdateStatus>('idle');

  /** Download progress percentage (0-100) */
  readonly downloadProgress = signal(0);

  /** Version of the currently active OTA bundle, or 'builtin' if using the original */
  readonly currentBundleVersion = signal<string>('builtin');

  /**
   * Bundle version of the web assets actually running: the applied OTA bundle,
   * or the bundle compiled into the native binary while on 'builtin'.
   */
  readonly activeBundleVersion = computed(() =>
    this.currentBundleVersion() === 'builtin' ? WEB_BUNDLE_VERSION : this.currentBundleVersion(),
  );

  /** Error message if the last operation failed */
  readonly lastError = signal<string | null>(null);

  /**
   * Initialize the OTA update system. Must be called on app startup
   * (after SplashScreen.hide() and before other initialization).
   *
   * CRITICAL: Calls notifyAppReady() to confirm the current bundle loaded
   * successfully. Without this, the plugin will auto-rollback to the
   * previous version after a timeout (default 10 seconds).
   */
  async initialize(): Promise<void> {
    if (!this.platformService.isNativeMobile()) {
      this.status.set('disabled');
      return;
    }

    try {
      // Confirm current bundle is working — prevents auto-rollback
      await CapacitorUpdater.notifyAppReady();

      // Get current bundle info
      const current = await CapacitorUpdater.current();
      this.currentBundleVersion.set(current.bundle.version);

      this.loggingService.logInfo(
        `OTA: Initialized. Current bundle: ${current.bundle.version}`,
      );

      // Register event listeners for update lifecycle
      this.registerEventListeners();
    } catch (err) {
      this.loggingService.logException(err, 'OTA: Initialization failed');
      this.status.set('error');
      this.lastError.set(err instanceof Error ? err.message : 'Initialization failed');
    }
  }

  /**
   * Check Azure Blob Storage for a newer bundle version.
   * If a newer version is found, downloads and applies it immediately.
   */
  async checkForUpdate(): Promise<void> {
    if (!this.platformService.isNativeMobile()) {
      return;
    }

    // Don't check for updates if initialization failed (notifyAppReady was not called)
    if (this.status() === 'error') {
      this.loggingService.logWarning('OTA: Skipping update check — initialization failed');
      return;
    }

    // Check kill-switch from Firebase Remote Config
    const config = this.remoteConfigService.config();
    if (!config.ota_updates_enabled) {
      this.loggingService.logInfo('OTA: Updates disabled via Remote Config');
      this.status.set('disabled');
      return;
    }

    // Prefer SAS-authenticated URL from Firebase Remote Config (rotatable without app store update).
    // Fall back to environment config URL if Remote Config hasn't provided one.
    const otaUpdateUrl = config.ota_manifest_url || this.env.appSettings.otaUpdateUrl;
    if (!otaUpdateUrl) {
      this.loggingService.logWarning('OTA: No otaUpdateUrl configured (checked Remote Config and environment config)');
      this.status.set('disabled');
      return;
    }

    this.status.set('checking');
    this.lastError.set(null);

    try {
      // Fetch the latest.json manifest from Azure Blob Storage via GET.
      // Uses CapacitorHttp.get() (native HTTP) instead of fetch() to bypass
      // CORS restrictions in the iOS WebView (capacitor://localhost origin).
      // The global CapacitorHttp plugin is disabled (for ServisBot compatibility),
      // but calling the API directly still routes through native code.
      const response = await CapacitorHttp.get({
        url: otaUpdateUrl,
        headers: { 'Cache-Control': 'no-cache' },
      });

      if (response.status < 200 || response.status >= 300) {
        throw new Error(`Failed to fetch OTA manifest: HTTP ${response.status}`);
      }

      const manifest: OtaManifest = typeof response.data === 'string'
        ? JSON.parse(response.data)
        : response.data;

      // Validate manifest
      if (!manifest.version || !manifest.url || !manifest.checksum) {
        throw new Error('Invalid OTA manifest: missing version, url, or checksum');
      }

      const currentVersion = this.currentBundleVersion();

      // Bundle versions are compared only against other bundle versions. The native
      // app version follows an independent, manually bumped scheme
      // (NATIVE_APP_VERSION) and must never take part in this comparison. On the
      // builtin bundle the baseline is the bundle version compiled into the binary,
      // so a fresh store install is never downgraded to older web assets.
      const baselineVersion = this.activeBundleVersion();

      if (!this.isNewerVersion(manifest.version, baselineVersion)) {
        this.loggingService.logInfo(
          `OTA: No update needed. Current: ${currentVersion} (${baselineVersion}), Latest: ${manifest.version}`,
        );
        this.status.set('idle');
        return;
      }

      // Optional guard: a bundle can declare the minimum native app version it
      // supports, so bundles needing new native code require a store update.
      if (manifest.minAppVersion) {
        const deviceDetails = await this.platformService.ensureDeviceDetails();
        const nativeVersion = deviceDetails?.appVersion;

        if (nativeVersion && this.isNewerVersion(manifest.minAppVersion, nativeVersion)) {
          this.loggingService.logWarning(
            `OTA: Bundle v${manifest.version} requires app version ${manifest.minAppVersion}, installed: ${nativeVersion}. Requires store update.`,
          );
          this.status.set('idle');
          return;
        }
      }

      this.loggingService.logInfo(
        `OTA: Update available. Current: ${currentVersion}, New: ${manifest.version}. Downloading...`,
      );

      // Download the bundle
      await this.downloadAndPrepare(manifest);
    } catch (err) {
      this.loggingService.logException(err, 'OTA: Update check failed');
      this.status.set('error');
      this.lastError.set(err instanceof Error ? err.message : 'Update check failed');
    }
  }

  /**
   * Download a bundle and apply it immediately.
   * Calls CapacitorUpdater.set() which reloads the WebView — no code
   * after set() will execute in the current JS context.
   */
  private async downloadAndPrepare(manifest: OtaManifest): Promise<void> {
    this.status.set('downloading');
    this.downloadProgress.set(0);

    try {
      // Download the bundle using the Capgo plugin
      const bundle = await CapacitorUpdater.download({
        url: manifest.url,
        version: manifest.version,
        checksum: manifest.checksum,
      });

      this.downloadProgress.set(100);

      this.loggingService.logInfo(
        `OTA: Bundle v${manifest.version} downloaded. Applying now — WebView will reload.`,
      );

      // Apply the bundle immediately by reloading the WebView.
      // set() destroys the current JS context and loads the new bundle right away,
      // so no code after this line will execute.
      await CapacitorUpdater.set(bundle);
    } catch (err) {
      this.status.set('error');
      this.lastError.set(err instanceof Error ? err.message : 'Download failed');
      this.loggingService.logException(err, 'OTA: Bundle download failed');
    }
  }

  /**
   * Register event listeners for Capgo plugin events.
   * These provide observability into the update lifecycle.
   */
  private registerEventListeners(): void {
    if (this.listenersRegistered) {
      return;
    }
    this.listenersRegistered = true;

    CapacitorUpdater.addListener('download', (info: { percent: number }) => {
      this.downloadProgress.set(info.percent);
    });

    CapacitorUpdater.addListener('updateFailed', (info: { bundle: BundleInfo }) => {
      this.loggingService.logWarning(
        `OTA: Update failed, auto-rolled back from v${info.bundle.version}`,
      );
      this.status.set('error');
      this.lastError.set(`Update to v${info.bundle.version} failed, rolled back`);
    });
  }

  /**
   * Compare two semantic versions. Returns true if `newVersion` is newer than `currentVersion`.
   */
  private isNewerVersion(newVersion: string, currentVersion: string): boolean {
    const newParts = this.parseVersion(newVersion);
    const currentParts = this.parseVersion(currentVersion);

    for (let i = 0; i < Math.max(newParts.length, currentParts.length); i++) {
      const newPart = newParts[i] ?? 0;
      const currentPart = currentParts[i] ?? 0;

      if (newPart > currentPart) return true;
      if (newPart < currentPart) return false;
    }

    return false; // versions are equal
  }

  /**
   * Parse a version string into an array of numeric components, treating `.` and
   * `-` as separators so both semver (`1.211.0`) and the date-based tag scheme
   * (`2026-07-24.1`) compare correctly. Parsing stops at the first non-numeric
   * component, so prerelease labels such as `1.2.0-beta.1` are ignored.
   */
  private parseVersion(version: string): number[] {
    const parts: number[] = [];

    for (const component of version.split(/[.\-+]/)) {
      const parsed = parseInt(component, 10);
      if (isNaN(parsed)) break;
      parts.push(parsed);
    }

    return parts;
  }
}
